import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import PageContainer from '../../common_components/PageContainer';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import AccessRestrictedContent from '../../common_components/AccessRestrictedContent';
import { toast } from '../../common_components/Toast/ToastUtils';
import { fetchAgendas, deleteAgendaThunk, prepareAgendaThunk, fetchVisibleClients, fetchAgendaJobStatus, isAgendaJobRunning, selectAgendas, selectAgendasLoading, selectAgendasTotal, selectAgendaDeleting, selectAgendaPreparing, } from '../../redux/meetingAgendaSlice';
import { fetchDesignationWiseUsers } from '../../redux/clientDataSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import { selectUser } from '../../redux/authSlice';
import ActionMenu from './components/ActionMenu';
import ViewAgendaDrawer from './components/ViewAgendaDrawer';
import NewAgendaDrawer from './components/NewAgendaDrawer';
import MeetingAgendaHeader from './components/MeetingAgendaHeader';
/**
 * Meeting Agenda — list page (Phase 1).
 *
 * Columns: Client Name | Created By | Date | Actions.
 *
 * Permissions: `client_scope` decides which clients' agendas are in range and
 * the "Full Access" action decides whose agendas are visible — both enforced
 * server-side. Full Access is mirrored here only to decide whether Delete is
 * offered on rows the user didn't create.
 *
 * Client / AM filters and the New Agenda button land in Phases 2–3.
 */
const MODULE_KEY = 'meeting_agenda';
const FULL_ACCESS = 'Full Access';
const ITEMS_PER_PAGE = 20;
// MM/DD/YYYY in US Eastern, matching the drawers and the rest of the portal.
const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
});
/**
 * YYYY-MM-DD → MM/DD/YYYY, formatted from the string itself. These are plain
 * calendar days with no time: parsing one into a Date would place it at UTC
 * midnight and the Eastern formatter would render the day before.
 */
const fmtYmd = (ymd) => {
    if (!ymd)
        return '';
    const [y, m, d] = String(ymd).slice(0, 10).split('-');
    return y && m && d ? `${m}/${d}/${y}` : String(ymd);
};
const fmtDate = (iso) => {
    if (!iso)
        return '—';
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (isNaN(d.getTime()))
        return '—';
    return DATE_FORMATTER.format(d);
};
/**
 * Transient progress chip shown only while a row's documents are being
 * generated. There is no saved/unsaved state to advertise — every agenda is
 * created by preparing it — so nothing is shown once that finishes.
 */
const PreparingPill = () => (_jsx(Box, { sx: {
        px: 0.75,
        py: 0.25,
        borderRadius: 999,
        bgcolor: '#FFF8E1',
        color: '#93370D',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    }, children: "Preparing\u2026" }));
const COLUMNS = [
    { id: 'client_name', label: 'Client Name', width: '320px', sortable: true, align: 'left' },
    { id: 'created_by_name', label: 'Created By', width: '240px', sortable: true, align: 'left' },
    // Not sortable: the value lives on a sub-document the list sort whitelist
    // doesn't cover, and a header that does nothing is worse than none.
    { id: 'report_range', label: 'Report Range', width: '230px', sortable: false, align: 'left' },
    { id: 'created_at', label: 'Created Date', width: '180px', sortable: true, align: 'left' },
    { id: 'action', label: 'Actions', width: '90px', sortable: false, align: 'center' },
];
const MeetingAgendaList = () => {
    const dispatch = useDispatch();
    const agendas = useSelector(selectAgendas);
    const loading = useSelector(selectAgendasLoading);
    const total = useSelector(selectAgendasTotal);
    const deleting = useSelector(selectAgendaDeleting);
    const preparingId = useSelector(selectAgendaPreparing);
    const permissions = useSelector((state) => state.auth.permissions);
    const user = useSelector(selectUser);
    const modulePerms = permissions?.[MODULE_KEY];
    const hasAccess = modulePerms?.access !== false;
    const hasFullAccess = Array.isArray(modulePerms?.actions)
        ? modulePerms.actions.includes(FULL_ACCESS)
        : false;
    const currentUserId = String(user?._id ?? user?.id ?? '');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedAmId, setSelectedAmId] = useState('');
    const [sortConfig, setSortConfig] = useState({
        columnId: 'created_at',
        direction: 'desc',
    });
    const [viewAgendaId, setViewAgendaId] = useState(null);
    const [newDrawerOpen, setNewDrawerOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const isFirstLoad = useRef(true);
    // setActiveModuleKey is a plain action, so the module key is in the store
    // before the client fetch below reads it for the x-module-key header.
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
        if (!hasAccess)
            return;
        dispatch(fetchVisibleClients());
    }, [dispatch, hasAccess]);
    // AM list only matters when the AM filter is actually rendered.
    useEffect(() => {
        if (!hasAccess || !hasFullAccess)
            return;
        dispatch(fetchDesignationWiseUsers());
    }, [dispatch, hasAccess, hasFullAccess]);
    const loadAgendas = useCallback(async () => {
        try {
            await dispatch(fetchAgendas({
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                search: searchTerm || undefined,
                client_id: selectedClientId || undefined,
                am_id: hasFullAccess && selectedAmId ? selectedAmId : undefined,
                sort: sortConfig?.columnId,
                order: sortConfig?.direction,
            })).unwrap();
        }
        catch {
            /* slice stores the error; toast would double-report on every poll */
        }
        finally {
            isFirstLoad.current = false;
        }
    }, [
        dispatch,
        currentPage,
        searchTerm,
        selectedClientId,
        selectedAmId,
        hasFullAccess,
        sortConfig,
    ]);
    useEffect(() => {
        if (!hasAccess)
            return;
        loadAgendas();
    }, [hasAccess, loadAgendas]);
    // A filter change starts over at page 1.
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedClientId, selectedAmId]);
    const handleSortChange = useCallback((columnId, direction) => {
        setCurrentPage(1);
        setSortConfig(direction ? { columnId, direction } : null);
    }, []);
    /** Retry / regenerate from a list row — the drawer path does this inline. */
    /**
     * Re-preparing regenerates both documents from scratch, so any hand edits
     * are discarded. The original AI output is kept server-side, but the
     * edits themselves are not — so confirm before throwing them away.
     */
    const [overwriteTarget, setOverwriteTarget] = useState(null);
    const handlePrepareClick = (row) => {
        if (row.edited_at) {
            setOverwriteTarget(row);
            return;
        }
        handlePrepare(row);
    };
    const handlePrepare = async (row) => {
        setOverwriteTarget(null);
        try {
            const res = await dispatch(prepareAgendaThunk(row._id)).unwrap();
            // Queued, not finished — the poll below reports when it lands.
            toast.info(res.message || 'Preparing the agenda — this takes a few minutes.');
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Failed to prepare the agenda');
        }
    };
    /**
     * Poll any row whose preparation is still running. Generation takes
     * minutes in the worker, so the row's job is the only way to know when the
     * documents land. Polling stops the moment nothing is in flight.
     */
    const pollRef = useRef(null);
    /** Agenda ids observed in a running state during this page's lifetime. */
    const watchedRef = useRef(new Set());
    useEffect(() => {
        const running = agendas.filter((a) => isAgendaJobRunning(a.job));
        if (!running.length) {
            if (pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        if (pollRef.current)
            return;
        pollRef.current = window.setInterval(() => {
            agendas
                .filter((a) => isAgendaJobRunning(a.job))
                .forEach((a) => dispatch(fetchAgendaJobStatus(a._id)));
        }, 5000);
        return () => {
            if (pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [agendas, dispatch]);
    /**
     * Announce a run that FINISHED WHILE WE WERE WATCHING.
     *
     * Only agendas seen in a running state during this page's lifetime are
     * announced. A job that was already done or failed when the list loaded is
     * history, not news — announcing those is why a plain page refresh
     * re-popped yesterday's success and failure toasts.
     */
    useEffect(() => {
        agendas.forEach((a) => {
            const status = a.job?.status;
            if (isAgendaJobRunning(a.job)) {
                watchedRef.current.add(a._id);
                return;
            }
            if (status !== 'done' && status !== 'failed')
                return;
            // Never saw it running, so it finished before this page existed.
            if (!watchedRef.current.has(a._id))
                return;
            watchedRef.current.delete(a._id);
            if (status === 'done') {
                toast.success(`${a.client_name} agenda is ready.`);
            }
            else {
                toast.error(a.job?.error || `Could not prepare the ${a.client_name} agenda.`);
            }
        });
    }, [agendas]);
    const handleDeleteConfirm = async () => {
        if (!deleteTarget)
            return;
        try {
            const res = await dispatch(deleteAgendaThunk(deleteTarget._id)).unwrap();
            toast.success(res.message || 'Meeting agenda deleted');
            setDeleteTarget(null);
            loadAgendas();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Delete failed');
        }
    };
    const renderCell = (row, col) => {
        switch (col.id) {
            case 'client_name':
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                fontSize: '14px !important',
                                fontWeight: 400,
                                color: '#101828',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }, title: row.client_name, children: row.client_name || '—' }), preparingId === row._id && _jsx(PreparingPill, {})] }));
            case 'report_range': {
                const range = row.report_range;
                if (!range) {
                    return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#101828' }, children: "\u2014" }));
                }
                const hasCompare = !!(range.compare_start && range.compare_end);
                return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.25 }, children: [_jsxs(Typography, { sx: {
                                fontSize: '14px !important',
                                color: '#101828',
                                whiteSpace: 'nowrap',
                            }, children: [fmtYmd(range.start), " \u2013 ", fmtYmd(range.end)] }), hasCompare && (_jsxs(Typography, { sx: {
                                fontSize: '12px !important',
                                color: '#667085',
                                whiteSpace: 'nowrap',
                            }, children: ["vs ", fmtYmd(range.compare_start), " \u2013 ", fmtYmd(range.compare_end)] }))] }));
            }
            case 'created_by_name':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#101828' }, children: row.created_by_name || '—' }));
            case 'created_at':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#101828' }, children: fmtDate(row.created_at) }));
            case 'action':
                return (_jsx(ActionMenu, { row: row, canDelete: hasFullAccess || String(row.created_by) === currentUserId, preparing: preparingId === row._id, onView: (r) => setViewAgendaId(r._id), onPrepare: handlePrepareClick, onDelete: (r) => setDeleteTarget(r) }));
            default:
                return null;
        }
    };
    if (!hasAccess) {
        return (_jsx(PageContainer, { children: _jsx(AccessRestrictedContent, {}) }));
    }
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    height: 'calc(100vh - 90px)',
                    overflow: 'hidden',
                }, children: [_jsx(MeetingAgendaHeader, { clientId: selectedClientId, setClientId: setSelectedClientId, amId: selectedAmId, setAmId: setSelectedAmId, showAmFilter: hasFullAccess, onNewAgenda: () => setNewDrawerOpen(true) }), _jsxs(Paper, { elevation: 0, sx: {
                            border: '1px solid #D0D5DD',
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                        }, children: [_jsx(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 0,
                                    borderBottom: '1px solid #EAECF0',
                                    flexShrink: 0,
                                }, children: _jsx(TableToolbar, { title: "", showSearch: true, onSearchChange: (val) => {
                                        setSearchTerm(val);
                                        setCurrentPage(1);
                                    }, showRows: true, showExpand: false, showFullScreen: true }) }), _jsx(Box, { sx: {
                                    overflow: 'hidden',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    flex: 1,
                                    minHeight: 0,
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    // Give the table's scroll container a definite height so its
                                    // maxHeight:100% resolves and the body scrolls internally,
                                    // keeping the header row sticky.
                                    '& .reusable-table-container': {
                                        height: '100%',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                    },
                                }, children: _jsx(CommonTable, { columns: COLUMNS, rows: agendas, sortConfig: sortConfig, onSortChange: handleSortChange, isLoading: loading, isFirstLoad: isFirstLoad.current, emptyMessage: "No meeting agendas yet.", renderCell: (row, col) => renderCell(row, col), stickyHeader: true, expandTrigger: "none", maxHeight: "100%", pinnedColumns: { left: [], right: ['action'] } }) }), !loading && total > 0 && (_jsx(Box, { sx: { p: 2, borderTop: '1px solid #EAECF0', flexShrink: 0 }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: Math.ceil(total / ITEMS_PER_PAGE), onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: total, itemsPerPage: ITEMS_PER_PAGE, showEntriesMessage: true }) }))] })] }), _jsx(ViewAgendaDrawer, { open: !!viewAgendaId, agendaId: viewAgendaId, onClose: () => setViewAgendaId(null) }), _jsx(NewAgendaDrawer, { open: newDrawerOpen, onClose: () => setNewDrawerOpen(false), defaultClientId: selectedClientId, onSuccess: loadAgendas, onPrepared: (agendaId) => setViewAgendaId(agendaId) }), _jsx(ConfirmActionModal, { open: !!deleteTarget, title: "Delete Meeting Agenda", description: deleteTarget
                    ? `Are you sure you want to delete the meeting agenda for ${deleteTarget.client_name}? This cannot be undone.`
                    : '', confirmText: "Delete", onConfirm: handleDeleteConfirm, onClose: () => setDeleteTarget(null), loading: deleting, isDeleteIcon: true }), _jsx(ConfirmActionModal, { open: !!overwriteTarget, title: "Discard manual edits?", description: overwriteTarget
                    ? `The ${overwriteTarget.client_name} agenda was edited by ` +
                        `${overwriteTarget.edited_by_name || 'a team member'} on ` +
                        `${new Date(overwriteTarget.edited_at).toLocaleDateString()}. ` +
                        `Re-preparing rebuilds both documents from the database and those ` +
                        `edits will be lost. Continue?`
                    : '', confirmText: "Re-prepare", onConfirm: () => overwriteTarget && handlePrepare(overwriteTarget), onClose: () => setOverwriteTarget(null), loading: !!preparingId })] }));
};
export default MeetingAgendaList;
